import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container, Title, Text, Card, Badge, Button, Group, Stack, Drawer, Checkbox,
  ActionIcon, Tooltip, SimpleGrid,
} from '@mantine/core';
import {
  IconSearch, IconEye, IconAdjustments, IconPlus, IconRefresh, IconListDetails,
} from '@tabler/icons-react';
import { DateTimePicker } from '@mantine/dates';
import { Flex, Select, TextInput, CCMSDataTable, StreamProgress, StatusKPICard, StatusKPISkeleton, getStatusIcon, ListPagination } from '@/components';
import { EmptyState } from '@components/ccms-components/utils/EmptyState';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import type { SortConfig } from '@/components/ccms-components/types/common';
import { useNavigate } from 'react-router-dom';
import {
  useCareCaseService, CareCaseListView as CareCaseRow, CareCaseFilterModel,
  useCareNeedService, useLocationService, useACCareSettingService,
  useLeaderService, useCareCaseStatusService, CareCaseStatus,
  useCaseStreaming,
} from '@actschurch/ccms-sdk';
import { useSidebarCountsContext } from '@/contexts/SidebarCountsContext';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import { sortRows } from '@/utils/tableSort';
import { toast } from '@/components/ui/providers/AlertProvider';
import BulkDeleteControls from '@/components/common/BulkDeleteControls';
import RowDeleteAction from '@/components/common/RowDeleteAction';
import ExportCsvButton from '@/components/common/ExportCsvButton';
import { useBulkDelete } from '@/hooks/useBulkDelete';
import { useModuleDeleteSettings } from '@/hooks/useModuleDeleteSettings';
import { AccessControl } from '@/components';

const PAGE_SIZE = 10;

/**
 * Statuses that get a KPI card, in display order (an "All" card is prepended).
 *
 * ⚠️ Matched on name because the assigned `statusID`s for Waiting List / Draft aren't fixed constants
 * the way New(0)/Assigned(1)/In Progress(2) are — renaming one of these in /LookUp will drop its card.
 * If those IDs get pinned down, switch this to an ID list.
 */
const KPI_STATUS_NAMES = ['new', 'assigned', 'waiting list', 'draft'];

/**
 * The applied filter, widened with `locationID`.
 *
 * ⚠️ `CareCaseFilterModel` has **no** `locationID`, so location cannot be pushed to the server. It is
 * carried here and applied in memory instead, which costs nothing on this page because every other
 * filter is already client-side — the case backend ignores filters on `listView`, so the rows are
 * streamed unfiltered regardless. `CareCaseListView` does carry `locationID`, so the match is exact
 * rather than a name comparison.
 */
type AppliedCaseFilter = Partial<CareCaseFilterModel> & { locationID?: string };

/** Cards actually rendered: the statuses above plus "All". Drives the grid width and the skeletons. */
const KPI_CARD_COUNT = KPI_STATUS_NAMES.length + 1;

function toSDKDate(value: string | Date | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return String(d.getFullYear()) + pad(d.getMonth() + 1) + pad(d.getDate()) + '000000';
}

/** `yyyyMMdd000000` (or any SDK timestamp) to the comparable `yyyyMMdd` number, for range filters. */
function sdkDayNumber(value: string | number | null | undefined): number | null {
  const raw = String(value ?? '').slice(0, 8);
  if (raw.length < 8) return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

function formatSDKDate(value: string | number | null | undefined): string {
  const raw = String(value ?? '');
  if (raw.length < 8) return '—';
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
}

interface CasesListViewProps {
  /** Locks the page to a single statusID (e.g. 0 = New, 1 = Assigned, 2 = In Progress, 3 = Resolved).
   *  When set, the KPI cards are hidden and the drawer's Status field is hidden/pinned. */
  fixedStatusID?: number;
  title?: string;
  subtitle?: string;
}

export function CasesListView({ fixedStatusID, title, subtitle }: CasesListViewProps) {
  const navigate         = useNavigate();
  const careCaseService  = useCareCaseService();
  const statusService    = useCareCaseStatusService();
  const { refetch } = useSidebarCountsContext();

  const CareNeeds = useCareNeedService();
  const Locations = useLocationService();
  const settingService = useACCareSettingService();
  const deleteSettings = useModuleDeleteSettings(settingService, 'Cases');
  const bulk = useBulkDelete(careCaseService, deleteSettings.allowBulkDelete, 'case');
  const Leaders   = useLeaderService();

  const showStatusKPIs = fixedStatusID === undefined;

  // ── Table / pagination / sort ──────────────────────────────────────────
  const [activePage, setActivePage] = useState(1);
  const [pageSize, setPageSize]     = useState(PAGE_SIZE);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  // ── Filter draft state ───────────────────────────────────────────────────
  const [searchTerm, setSearchTerm]                 = useState('');
  const [filtersOpen, setFiltersOpen]               = useState(false);
  const [filterStatus, setFilterStatus]             = useState(
    fixedStatusID !== undefined ? String(fixedStatusID) : '',
  );
  const [filterNeedID, setFilterNeedID]             = useState('');
  const [filterLocationID, setFilterLocationID]     = useState('');
  const [filterLeaderUserId, setFilterLeaderUserId] = useState('');
  const [filterStartDate, setFilterStartDate]       = useState<Date | null>(null);
  const [filterEndDate, setFilterEndDate]           = useState<Date | null>(null);
  const [appliedFilter, setAppliedFilter]           = useState<AppliedCaseFilter>(
    fixedStatusID !== undefined ? { statusID: String(fixedStatusID) } : {},
  );

  // "Clear Filters" should ignore the fixed status — it's not a user-chosen filter here
  const isFilterActive = Boolean(
    (showStatusKPIs && appliedFilter.statusID) || appliedFilter.needID ||
    appliedFilter.locationID || appliedFilter.leaderUserId ||
    appliedFilter.startDate || appliedFilter.endDate
  );

  // ── Lookup data (filter drawer + KPI) ────────────────────────────────────
  const [needs, setNeeds]         = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [leaders, setLeaders]     = useState<any[]>([]);
  const [statuses, setStatuses]           = useState<CareCaseStatus[]>([]);
  const [statusesLoading, setStatusesLoading] = useState(true);

  // Include-list rather than exclude-list: only the five statuses worth a KPI card, ordered to match
  // KPI_STATUS_NAMES so the row reads New → Assigned → In Progress → Waiting List → Draft.
  const visibleStatuses = useMemo(
    () =>
      statuses
        .filter((status) =>
          KPI_STATUS_NAMES.includes((status.name ?? '').trim().toLowerCase()),
        )
        .sort(
          (a, b) =>
            KPI_STATUS_NAMES.indexOf((a.name ?? '').trim().toLowerCase()) -
            KPI_STATUS_NAMES.indexOf((b.name ?? '').trim().toLowerCase()),
        ),
    [statuses],
  );

  // ── Status counts for KPI cards (global, independent of search/filter) ───
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [allCount, setAllCount]         = useState(0);
  const [countsLoading, setCountsLoading] = useState(true);

  // ── Stream cases ─────────────────────────────────────────────────────────
  // Streamed **unfiltered**, once, in 50-row batches. Every filter is then applied in memory over
  // the result.
  //
  // The drawer filters used to be pushed into this stream, but the backend does not honour them on
  // `listView`, so applying a filter re-fetched and returned the same unfiltered rows. Filtering
  // locally is both correct and instant, and the page already holds the whole set for search.
  const {
    items: cases,
    isLoading: loading,
    progress,
    loadedCount,
    load: loadCases,
  } = useCaseStreaming(undefined, 50);

  // Kept in a ref so the sidebar-count refresh doesn't participate in the stream effect's deps.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    loadCases().then(() => refetchRef.current());
  }, [loadCases]);

  useEffect(() => { setActivePage(1); }, [searchTerm, appliedFilter]);

  // ── Fetch lookup data for filter drawer ───────────────────────────────────
  const fetchLookups = useCallback(async () => {
    try {
      // Geography is filtered by **Location** only. Zone and Area were both dropped at the care
      // office's request — Location is the split they actually work by. `CareCaseListView` carries
      // `locationID`, so the match is exact.
      const [n, l, ld] = await Promise.all([
        CareNeeds.getAll(), Locations.getAll(), Leaders.list(),
      ]);
      setNeeds(n ?? []); setLocations(l ?? []); setLeaders(ld ?? []);
    } catch (e) {
      console.error('Error fetching lookups:', e);
    }
    setStatusesLoading(true);
    try {
      const s = await statusService.getAll();
      setStatuses((s as any)?.data ?? s ?? []);
    } catch (e) {
      console.error('Error fetching case statuses:', e);
    } finally {
      setStatusesLoading(false);
    }
  }, [CareNeeds, Locations, Leaders, statusService]);

  useEffect(() => { fetchLookups(); }, [fetchLookups]);

  // ── Fetch global per-status counts for KPI cards (All Cases page only) ───
  const fetchStatusCounts = useCallback(async () => {
    if (!showStatusKPIs || statuses.length === 0) return;
    setCountsLoading(true);
    try {
      const [allRes, ...perStatusRes] = await Promise.all([
        careCaseService.listView({ take: 1, skip: 0 }),
        ...visibleStatuses.map((s) =>
          careCaseService.listView({ take: 1, skip: 0, statusID: String(s.statusID) })
        ),
      ]);
      setAllCount(allRes.totalCount ?? 0);
      const counts: Record<string, number> = {};
      visibleStatuses.forEach((s, i) => { counts[String(s.statusID)] = perStatusRes[i]?.totalCount ?? 0; });
      setStatusCounts(counts);
    } catch (e) {
      console.error('Error fetching status counts:', e);
    } finally {
      setCountsLoading(false);
    }
  }, [showStatusKPIs, statuses, visibleStatuses, careCaseService]);

  useEffect(() => { fetchStatusCounts(); }, [fetchStatusCounts]);

  // ── Filter handlers ───────────────────────────────────────────────────────
  const handleApplyFilters = () => {
    setAppliedFilter({
      statusID:     fixedStatusID !== undefined ? String(fixedStatusID) : filterStatus,
      needID:       filterNeedID,
      locationID:   filterLocationID,
      leaderUserId: filterLeaderUserId,
      startDate:    toSDKDate(filterStartDate),
      endDate:      toSDKDate(filterEndDate),
    });
    setActivePage(1);
    setFiltersOpen(false);
  };

  const handleClearFilters = () => {
    setFilterStatus(fixedStatusID !== undefined ? String(fixedStatusID) : '');
    setFilterNeedID(''); setFilterLocationID(''); setFilterLeaderUserId('');
    setFilterStartDate(null); setFilterEndDate(null);
    setAppliedFilter(fixedStatusID !== undefined ? { statusID: String(fixedStatusID) } : {});
    setActivePage(1);
  };

  // ── Client-side filtering over the streamed set ───────────────────────────
  // `fixedStatusID` is applied here too, so the status-scoped pages need no separate request.
  const filteredCases = useMemo(() => {
    const wantStatus = fixedStatusID !== undefined ? String(fixedStatusID) : appliedFilter.statusID;
    const from = sdkDayNumber(appliedFilter.startDate);
    const to = sdkDayNumber(appliedFilter.endDate);

    return cases.filter((row) => {
      if (wantStatus && String(row.statusID) !== String(wantStatus)) return false;
      if (appliedFilter.needID && String(row.needID) !== String(appliedFilter.needID)) return false;
      if (appliedFilter.locationID && String(row.locationID ?? '') !== String(appliedFilter.locationID)) {
        return false;
      }
      if (appliedFilter.leaderUserId && String(row.userId ?? '') !== String(appliedFilter.leaderUserId)) {
        return false;
      }

      if (from || to) {
        const opened = Number(String(row.caseDateTime ?? '').slice(0, 8));
        if (!opened) return false;
        if (from && opened < from) return false;
        if (to && opened > to) return false;
      }

      return true;
    });
  }, [cases, fixedStatusID, appliedFilter]);

  // ── Client-side search over the filtered set ──────────────────────────────
  const searchedCases = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return filteredCases;
    return filteredCases.filter((row) =>
      [row.name, row.leaderName, row.needName, row.locationName, row.counseleeNames, row.statusName]
        .some((field) => (field ?? '').toLowerCase().includes(term))
      || String(row.caseID ?? '').includes(term)
    );
  }, [filteredCases, searchTerm]);

  // ── Client-side sort over the streamed set ────────────────────────────────
  // ── Column definitions ──────────────────────────────────────────────────
  /**
   * ⚠️ `CareCaseService` carries no case-level `delete` yet — only `deleteNote`/`deleteFile`. The action
   * therefore renders disabled with a reason rather than being absent, and starts working the moment
   * the endpoint lands. Detected separately from the bulk flow, since the two may ship apart.
   */
  const canDeleteOne = typeof (careCaseService as { delete?: unknown }).delete === 'function';

  // ⚠️ Deliberately does **not** catch — `RowDeleteAction` words the failure and keeps its dialog open.
  const deleteOne = useCallback(async (caseID: number) => {
    await (careCaseService as unknown as { delete: (id: number) => Promise<unknown> }).delete(caseID);
    await loadCases();
    refetchRef.current();
    toast.success('Deleted', 'The case was removed.');
  }, [careCaseService, loadCases]);

  const columns: TableColumn<CareCaseRow>[] = [
    ...(bulk.selecting ? [{
      key: 'select',
      header: '',
      accessor: (row: CareCaseRow) => row.caseID,
      sortable: false,
      width: 44,
      render: (_: unknown, row: CareCaseRow) => (
  <div
    onClick={(e) => e.stopPropagation()}
    onMouseDown={(e) => e.stopPropagation()}
    onPointerDown={(e) => e.stopPropagation()}
    style={{ display: 'inline-flex' }}
  >
    <Checkbox
      checked={bulk.isSelected(row.caseID)}
      onChange={() => bulk.toggle(row.caseID)}
    />
  </div>
),
    } as TableColumn<CareCaseRow>] : []),
    { key: 'caseID', header: 'ID', accessor: (row) => row.caseID, sortable: true, width: 80 },
    { key: 'caseName', header: 'Case Name', accessor: (row) => row.name ?? '—', sortable: true },
    {
      key: 'caseOpened', header: 'Date Opened',
      accessor: (row) => String(row.caseDateTime ?? ''), sortable: true,
      exportAccessor: (row) => formatSDKDate(row.caseDateTime),
      render: (value) => formatSDKDate(value as string),
    },
    {
  key: 'statusName', header: 'Status', accessor: (row) => row.statusName ?? '—',
  sortable: true, align: 'center',
  render: (value, row) => (
    <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(row.statusColor)}>
      {value}
    </Badge>
  ),
},
    { key: 'needName', header: 'Counseling Need', accessor: (row) => row.needName ?? '—', sortable: true },
    {
      key: 'locationName', header: 'Location', accessor: (row) => row.locationName ?? '—', sortable: true,
      render: (value, row) => (row.locationName
        ? (
          <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(row.locationColor)}>
            {value}
          </Badge>
        )
        : '—'),
    },
    { key: 'leaderName', header: 'Leader', accessor: (row) => row.leaderName ?? '—', sortable: true },
    { key: 'sessionCount', header: 'Sessions', accessor: (row) => row.sessionCount ?? 0, sortable: true, align: 'center' },
    {
      key: 'actions', header: 'Actions', accessor: (row) => row, sortable: false, align: 'center',
      render: (_, row) => (
        <Group gap={6} justify="center" wrap="nowrap">
          <Button variant="light" color="blue" size="xs"
            onClick={(e) => { e.stopPropagation(); navigate(`/Counselling/Cases/${row.caseID}`); }}>
            <IconEye size={16} />
          </Button>
          <AccessControl roles={['Admin']} mode='hide'>
          <RowDeleteAction
            id={row.caseID}
            entityLabel="case"
            recoverable
            recordLabel={row.name || `case #${row.caseID}`}
            confirmWord={deleteSettings.confirmWord}
            allowed={deleteSettings.allowDelete}
            supported={canDeleteOne}
            onDelete={deleteOne}
          />
          </AccessControl>
        </Group>
      ),
    },
  ];


  /**
   * Sorted by the clicked column's own `accessor`, so the sort key can never disagree with the cell.
   * ⚠️ This used to be a `switch` on the field name kept alongside the columns array; `caseID` was
   * marked sortable with no case in the switch, so clicking ID sorted every row by `''`.
   */
  const sortedCases = useMemo(
    () => sortRows(searchedCases, columns, sortConfig),
    [searchedCases, columns, sortConfig],
  );

  // ── Client-side pagination over the streamed set ──────────────────────────
  const totalCount = sortedCases.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    if (activePage > totalPages) setActivePage(totalPages);
  }, [activePage, totalPages]);

  const pagedCases = useMemo(() => {
    const firstIndex = (activePage - 1) * pageSize;
    return sortedCases.slice(firstIndex, firstIndex + pageSize);
  }, [sortedCases, activePage, pageSize]);

  /**
   * Noun for the empty states. Derived from the page title so the status-scoped pages read naturally
   * ("No new cases yet") rather than a flat "No cases yet".
   */
  const emptyEntityLabel = (title ?? '').toLowerCase().includes('case')
    ? (title ?? '').toLowerCase()
    : 'cases';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Container size="xl" py="xl">
      {/* ── Page header: title/subtitle left, Refresh far right ──
          `align="flex-end"` drops the icon onto the subtitle line rather than the title, so it sits
          directly above the last KPI card. */}
      <Group justify="space-between" align="flex-end" wrap="nowrap" mb="lg">
        <div>
          <Title order={2}>{title ?? 'Cases'}</Title>
          <Text c="dimmed">{subtitle ?? 'Manage and view all care support cases.'}</Text>
        </div>
        <Tooltip label="Refresh">
          <ActionIcon variant="subtle" onClick={() => loadCases()} loading={loading} size="lg">
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* ── Status KPI cards (All Cases page only) ──
          Column count comes from KPI_CARD_COUNT so it stays in step with KPI_STATUS_NAMES. It used to
          track the number of fetched statuses, which squeezed the cards until their labels truncated. */}
      {showStatusKPIs && (
        <SimpleGrid cols={{ base: 2, sm: 3, lg: KPI_CARD_COUNT }} spacing="md" mb="md">
          {(countsLoading || statusesLoading) ? (
            Array.from({ length: KPI_CARD_COUNT }).map((_, i) => <StatusKPISkeleton key={i} />)
          ) : (
            <>
              <StatusKPICard
                icon={IconListDetails}
                label="All"
                count={allCount}
                color="#4dabf7"
                active={filterStatus === ''}
                onClick={() => { setFilterStatus(''); setAppliedFilter((f) => { const { statusID, ...rest } = f; return rest; }); setActivePage(1); }}
              />
              {visibleStatuses.map((s) => (
                <StatusKPICard
                  key={s.statusID}
                  icon={getStatusIcon(s.icon)}
                  label={s.name}
                  count={statusCounts[String(s.statusID)] ?? 0}
                  color={s.color || '#868e96'}
                  active={filterStatus === String(s.statusID)}
                  onClick={() => {
                    const next = filterStatus === String(s.statusID) ? '' : String(s.statusID);
                    setFilterStatus(next);
                    setAppliedFilter((f) => ({ ...f, statusID: next || undefined }));
                    setActivePage(1);
                  }}
                />
              ))}
            </>
          )}
        </SimpleGrid>
      )}

      {/* ── Search & Filter bar ── */}
      <Card withBorder radius="md" padding="lg" mb="md">
        <Flex gap="sm" align="center" justify="space-between" wrap="wrap">
          <Flex gap="sm" align="center">
            <TextInput
              placeholder="Search by case name, leader, location…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              radius="md"
              styles={{ input: { width: '300px' } }}
              size="sm"
            />
            <Button variant="outline" leftSection={<IconAdjustments size={16} />} onClick={() => setFiltersOpen(true)} disabled={loading}>
              Filters
              {isFilterActive && <Badge ml="xs" size="xs" color="blue" circle>!</Badge>}
            </Button>
            {isFilterActive && (
              <Button variant="subtle" color="red" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
          </Flex>
          <Flex gap="sm" align="center" wrap="wrap">
            <AccessControl roles={['Admin']} mode='hide'>
            <BulkDeleteControls
              bulk={bulk}
              entityLabel="cases"
              confirmWord={deleteSettings.confirmWord}
              allowed={deleteSettings.allowBulkDelete}
              recoverable
            onDeleted={async ({ removed, requested, failure }) => {
                // Re-streams the set and refreshes the sidebar badges, same as the initial load.
                await loadCases();
                refetchRef.current();
                if (failure) return;
                if (removed === 0) toast.warning('Nothing deleted', 'No cases were removed.');
                else if (removed < requested) {
                  toast.warning('Some were skipped', `${removed} of ${requested} removed.`);
                } else {
                  toast.success('Deleted', `${removed} case${removed === 1 ? '' : 's'} removed.`);
                }
              }}
            />
            </AccessControl>
            <ExportCsvButton
              data={sortedCases}
              columns={columns}
              filename="cases"
            />
            <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/Counselling/Cases/CreateCaseWizard')}>
              Add Case
            </Button>
          </Flex>
        </Flex>
      </Card>

      {/* ── Filter Drawer ── */}
      <Drawer opened={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filter Cases" position="right" size="md" padding="md">
        <Stack>
          {/* Status field only shown on the All Cases page — sub-pages have it pinned */}
          {showStatusKPIs && (
            <Select
              label="Status"
              placeholder="All Statuses"
              data={[
                { value: '', label: 'All Statuses' },
                ...statuses.map((s) => ({ value: String(s.statusID), label: s.name })),
              ]}
              value={filterStatus}
              onChange={(v) => setFilterStatus(v ?? '')}
              clearable
            />
          )}
          <DateTimePicker label="Start Date" placeholder="Pick start date and time"
            value={filterStartDate} onChange={setFilterStartDate} clearable />
          <DateTimePicker label="End Date" placeholder="Pick end date and time"
            value={filterEndDate} onChange={setFilterEndDate} clearable />
          <Select label="Counseling Need" placeholder="All Needs"
            data={needs.map((n) => ({ value: String(n.needID), label: n.name ?? `Need ${n.needID}` }))}
            value={filterNeedID} onChange={(v) => setFilterNeedID(v ?? '')} searchable clearable />
          <Select label="Location" placeholder="All Locations"
            data={locations.map((l) => ({ value: String(l.locationID), label: l.name ?? `Location ${l.locationID}` }))}
            value={filterLocationID} onChange={(v) => setFilterLocationID(v ?? '')} searchable clearable />
          <Select label="Leader" placeholder="All Leaders"
            data={leaders.map((l) => {
              const name = [l.firstName, l.surname].filter(Boolean).join(' ').trim() || l.fullName?.trim() || l.userId;
              return { value: String(l.userId), label: name };
            })}
            value={filterLeaderUserId} onChange={(v) => setFilterLeaderUserId(v ?? '')} searchable clearable />
          <Group justify="flex-end" mt="sm">
            <Button variant="light" onClick={() => { handleClearFilters(); setFiltersOpen(false); }}>Clear</Button>
            <Button onClick={handleApplyFilters}>Apply</Button>
          </Group>
        </Stack>
      </Drawer>

      {/* ── Table card ── */}
      <Card withBorder radius="md" padding="lg">
        <StreamProgress
          loading={loading}
          progress={progress}
          loadedCount={loadedCount}
          label="cases"
        />
        {/* Same empty treatment as Pending Enrolments: one plain "nothing here yet" message, using
            the component's default copy. */}
        {!loading && sortedCases.length === 0 ? (
          <EmptyState variant="no-data" entityLabel={emptyEntityLabel} />
        ) : (
          <>
            <CCMSDataTable
              data={pagedCases}
              columns={columns}
              loading={loading && cases.length === 0}
              sortable
              onSort={(config) => { setSortConfig(config); setActivePage(1); }}
              onRowClick={(row) => navigate(`/Counselling/Cases/${row.caseID}`)}
              striped
              highlightOnHover
            />
            <ListPagination
              page={activePage}
              pageSize={pageSize}
              totalItems={totalCount}
              onPageChange={(page) => setActivePage(page)}
              onPageSizeChange={(size) => { setPageSize(size); setActivePage(1); }}
              showPageSize
            />
          </>
        )}
      </Card>
    </Container>
  );
}